import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { merchantGroupsApi, merchantOperatorsApi } from 'api';
import {
  CloseDialogResult,
  CopyText,
  CopyTextId,
  CrudPage,
  DataGridColumnDefinition,
  Dialog,
  FormControls,
  FormikMultiSelect,
  FormikTextField,
  MailLink,
  UserStatusLabel,
} from 'components';
import { QueryKey, SecureConfirmType, UserStatus } from 'enums';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { MerchantOperator } from 'types';
import { validationUtils } from 'utils';

type OperatorForm = {
  email: string;
  name: string;
  groupIds: string[];
};

const EMPTY_FORM: OperatorForm = { email: '', name: '', groupIds: [] };

export const MerchantOperatorsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Merchant, {
    keyPrefix: 'pages.operators',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const queryClient = useQueryClient();

  const queryResult = useQuery(
    QueryKey.MerchantOperators,
    merchantOperatorsApi.getAll,
  );
  const queryResultGroups = useQuery(
    QueryKey.MerchantGroups,
    merchantGroupsApi.getAll,
  );
  const groupsOptions = useMemo(
    () =>
      (queryResultGroups.data || []).map((group) => ({
        value: group.id,
        label: group.name,
      })),
    [queryResultGroups.data],
  );

  const [editState, setEditState] = useState<{
    open: boolean;
    id?: string;
    form: OperatorForm;
  }>({ open: false, form: EMPTY_FORM });
  const [removeState, setRemoveState] = useState<{
    open: boolean;
    data?: MerchantOperator;
  }>({ open: false });
  const [password, setPassword] = useState<string | null>(null);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.MerchantOperators);
  }, [queryClient]);

  const closeEdit = useCallback(
    () => setEditState({ open: false, form: EMPTY_FORM }),
    [],
  );

  const { mutate: createOperator, isLoading: isCreating } = useMutation(
    merchantOperatorsApi.create,
    {
      onSuccess: (result: { password: string }) => {
        closeEdit();
        setPassword(result.password);
        invalidate();
      },
      onError: (error: AxiosError) => {
        formik.setErrors(validationUtils.getFormErrors(error));
      },
    },
  );
  const { mutate: updateOperator, isLoading: isUpdating } = useMutation(
    merchantOperatorsApi.update,
    {
      onSuccess: () => {
        closeEdit();
        invalidate();
      },
      onError: (error: AxiosError) => {
        formik.setErrors(validationUtils.getFormErrors(error));
      },
    },
  );
  const { mutate: activate } = useMutation(merchantOperatorsApi.activate, {
    onSuccess: invalidate,
  });
  const { mutate: deactivate } = useMutation(merchantOperatorsApi.deactivate, {
    onSuccess: invalidate,
  });
  const { mutate: resetPassword } = useMutation(
    merchantOperatorsApi.resetPassword,
    {
      requireSecureConfirm: true,
      secureConfirmTypes: [SecureConfirmType.TwoFA],
      onSuccess: (result: { password: string }) => setPassword(result.password),
    },
  );
  const { mutate: removeOperator, isLoading: isRemoving } = useMutation(
    merchantOperatorsApi.remove,
    {
      onSuccess: () => {
        setRemoveState({ open: false });
        invalidate();
      },
    },
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().trim().required(tCommon('errors.required')),
        email: editState.id
          ? Yup.string()
          : Yup.string()
              .email(tCommon('errors.invalid'))
              .required(tCommon('errors.required')),
        groupIds: Yup.array().of(Yup.string()),
      }),
    [tCommon, editState.id],
  );

  const openCreate = useCallback(
    () => setEditState({ open: true, form: EMPTY_FORM }),
    [],
  );
  const openEdit = useCallback(
    (item: MerchantOperator) =>
      setEditState({
        open: true,
        id: item.id,
        form: {
          email: item.user?.email || '',
          name: item.user?.name || '',
          groupIds: map(item.merchantGroups, 'id') as string[],
        },
      }),
    [],
  );

  const handleSubmit = useCallback(
    (values: OperatorForm, formikHelpers: FormikHelpers<OperatorForm>) => {
      const { id } = editState;
      const options = {
        onSuccess: () => formikHelpers.resetForm(),
        onSettled: () => formikHelpers.setSubmitting(false),
      };
      if (id) {
        updateOperator(
          { id, data: { name: values.name, groupIds: values.groupIds } },
          options,
        );
      } else {
        createOperator(values, options);
      }
    },
    [editState, createOperator, updateOperator],
  );

  const formik = useFormik<OperatorForm>({
    initialValues: editState.form,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const handleCloseRemove = useCallback(
    ({ ok, data }: CloseDialogResult<MerchantOperator>) => {
      if (ok && data) {
        removeOperator(data.id);
      } else {
        setRemoveState({ open: false });
      }
    },
    [removeOperator],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<MerchantOperator>[] => [
      {
        header: t('fields.id'),
        valueGetter: (item) => <CopyTextId id={item.id} />,
      },
      {
        header: t('fields.name'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.user?.name}</div>
            <div>
              {item.user?.email && <MailLink email={item.user.email} />}
            </div>
          </Fragment>
        ),
      },
      {
        header: t('fields.status'),
        valueGetter: (item) =>
          item.user?.status && <UserStatusLabel status={item.user.status} />,
      },
      {
        header: t('fields.groups'),
        multiValueRenderer: {
          itemsGetter: (item) => item.merchantGroups || [],
          valueGetter: (group) => <div key={group.id}>{group.name}</div>,
        },
      },
      {
        header: t('fields.actions'),
        valueGetter: (item) => {
          const isActive = item.user?.status === UserStatus.Active;
          return (
            <Fragment>
              <IconButton color="primary" onClick={() => openEdit(item)}>
                <EditIcon />
              </IconButton>
              <IconButton
                color="secondary"
                onClick={() => resetPassword({ id: item.id })}
                title={t('actions.reset_password')}
              >
                <KeyIcon />
              </IconButton>
              {isActive ? (
                <IconButton
                  color="warning"
                  onClick={() => deactivate(item.id)}
                  title={t('actions.deactivate')}
                >
                  <BlockIcon />
                </IconButton>
              ) : (
                <IconButton
                  color="success"
                  onClick={() => activate(item.id)}
                  title={t('actions.activate')}
                >
                  <CheckCircleIcon />
                </IconButton>
              )}
              <IconButton
                color="error"
                onClick={() => setRemoveState({ open: true, data: item })}
              >
                <DeleteIcon />
              </IconButton>
            </Fragment>
          );
        },
      },
    ],
    [openEdit, resetPassword, activate, deactivate, t],
  );

  return (
    <Fragment>
      <CrudPage
        header={{
          title: t('title'),
          rightContentButton: { onClick: openCreate },
        }}
        table={{ queryResult, columns }}
      />

      <FormikProvider value={formik}>
        <Dialog
          open={editState.open}
          title={
            editState.id ? t('edit_dialog.title') : t('create_dialog.title')
          }
          okDisabled={!formik.isValid}
          disabled={isCreating || isUpdating}
          onClose={({ ok }: CloseDialogResult) => {
            if (ok) {
              formik.submitForm();
            } else {
              formik.resetForm();
              closeEdit();
            }
          }}
        >
          <Form>
            <FormControls>
              {!editState.id && (
                <FormikTextField
                  label={t('fields.email')}
                  name="email"
                  required
                />
              )}
              <FormikTextField label={t('fields.name')} name="name" required />
              <FormikMultiSelect
                label={t('fields.groups')}
                name="groupIds"
                options={groupsOptions}
              />
            </FormControls>
          </Form>
        </Dialog>
      </FormikProvider>

      <Dialog
        open={removeState.open}
        title={t('remove_dialog.title')}
        onClose={handleCloseRemove}
        disabled={isRemoving}
        data={removeState.data}
      >
        {removeState.data && <div>{removeState.data.user?.email}</div>}
      </Dialog>

      <Dialog
        open={!!password}
        title={t('password_dialog.title')}
        hideCancel
        onClose={() => setPassword(null)}
      >
        <Stack spacing={1} sx={{ mt: 1 }}>
          <div>{t('password_dialog.description')}</div>
          {password && <CopyText text={password} />}
        </Stack>
      </Dialog>
    </Fragment>
  );
};
